/**
 * iGraal — cashback and coupons while shopping
 *
 * Declares a single web-accessible-resource group exposing public/* (a module group) to
 * `<all_urls>` with no `use_dynamic_url` — so every file under public/, including images,
 * stylesheets and named feature chunks, is fetchable from any page at a fixed URL.
 *
 * public/images/icon.png is the cleanest signal: an image loaded through `probeImage`
 * with no CORS grant, surviving a restrictive page CSP. A successful load is conclusive —
 * the file is served only when the extension is installed.
 *
 * Manifest verified against version 10.1.5.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kmhkepipobnjllejbafajoemahjejdcm';

export const igraal: ExtensionDefinition = {
  id: ID,
  name: 'iGraal | Shopping with Savings',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'public/* exposed to <all_urls> with no dynamic URL. The icon image loads via <img> ' +
    'with no CORS grant, corroborated by the stable popup stylesheet and named chunks.',
  signals: [
    {
      id: 'war-icon-image',
      kind: 'web-accessible-resource',
      description:
        'Load public/images/icon.png via <img> — exposed to every origin and readable ' +
        'without a CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'public/images/icon.png'),
    },
    {
      id: 'war-popup-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch public/styles/popup.css — a stable, non-hashed stylesheet path exposed ' +
        'to every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'public/styles/popup.css'),
    },
    {
      id: 'war-cashback-chunk',
      kind: 'web-accessible-resource',
      description:
        'Fetch public/chunks/cashback.js — a vendor-named feature chunk, distinctive to ' +
        'iGraal and independent of the icon and stylesheet.',
      weight: 0.5,
      run: () => probeFetch(ID, 'public/chunks/cashback.js'),
    },
  ],
};
